function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

// name -> "name"; [locations, 0, SITE] -> "locations[0].SITE"
function formatFieldPath(path: ReadonlyArray<string>): string {
  return path.reduce((acc, segment) => {
    if (/^\d+$/.test(segment)) {
      return `${acc}[${segment}]`;
    }
    return acc.length > 0 ? `${acc}.${segment}` : segment;
  }, '');
}

function collectTreeErrors(node: unknown, path: ReadonlyArray<string>, messages: string[]): void {
  if (!isRecord(node)) {
    return;
  }

  if (isStringArray(node.errors)) {
    const prefix = path.length > 0 ? `${formatFieldPath(path)}: ` : '';
    for (const message of node.errors) {
      messages.push(`${prefix}${message}`);
    }
  }

  if (isRecord(node.properties)) {
    for (const key of Object.keys(node.properties)) {
      collectTreeErrors(node.properties[key], [...path, key], messages);
    }
  }

  if (Array.isArray(node.items)) {
    node.items.forEach((item, index) => {
      collectTreeErrors(item, [...path, String(index)], messages);
    });
  }
}

/**
 * Field-level validation messages from a tRPC error.
 *
 * The API formats Zod failures with `treeifyError` (Zod 4), which returns a nested
 * tree: `errors` at each node, `properties` for object fields, `items` for array
 * elements. We walk it recursively so root, nested-object and array-element errors
 * are all collected, each prefixed with its field path. `error.data` isn't typed
 * with the `zodError` field on the client, so we narrow from `unknown`.
 */
export function getZodFieldErrorMessages(error: unknown): string[] {
  if (!isRecord(error) || !isRecord(error.data)) {
    return [];
  }

  const zodError = error.data.zodError;
  if (!isRecord(zodError)) {
    return [];
  }

  const messages: string[] = [];
  collectTreeErrors(zodError, [], messages);
  return messages;
}
